import "./index.css";
import { clsx } from "clsx";
import { Info } from "lucide-react";
import { useMemo } from "react";
import { useTranslations } from "../../i18n";
import { Message } from "../Message";
import { Tag } from "../Tag";
import { Text } from "../Text";
import { Tooltip } from "../Tooltip";
import type { IFormFieldProps } from "./types";

export const FormField = ({
  children,
  label,
  description,
  message,
  messageColor = "danger",
  required = true,
  className,
  accessory,
  tooltipContent,
  htmlFor,
  labelId,
}: IFormFieldProps) => {
  const { t } = useTranslations();

  const rootClass = clsx("nuco-formField", className);

  const messageJSX = useMemo(() => {
    if (!message) return null;

    return (
      <Message
        className="nuco-formField__message"
        text={message}
        color={messageColor}
      />
    );
  }, [message, messageColor]);

  return (
    <div className={rootClass}>
      {label && (
        <div className="nuco-formField__top">
          <div className="nuco-formField__label">
            {htmlFor ? (
              <label
                id={labelId}
                htmlFor={htmlFor}
                className="nuco-formField__labelText nuco-text nuco-text--md"
              >
                {label}
              </label>
            ) : (
              <Text
                as="span"
                id={labelId}
                className="nuco-formField__labelText"
              >
                {label}
              </Text>
            )}
            {!required && (
              <Tag color="neutral" text={t("ui.formField.optional")} />
            )}
            {tooltipContent && (
              <div className="nuco-formField__tooltip">
                <Tooltip content={tooltipContent} placement="right-start">
                  <Info size="1rem" />
                </Tooltip>
              </div>
            )}
            {accessory && (
              <div className="nuco-formField__accessory">{accessory}</div>
            )}
          </div>
          {description && (
            <Text as="div" className="nuco-formField__description">
              {description}
            </Text>
          )}
        </div>
      )}
      <div className="nuco-formField__element">{children}</div>
      {messageJSX}
    </div>
  );
};
